// chat.js — Chat rendering, slash commands, emoji reactions for Peerly
// All rendering uses safe DOM APIs. No innerHTML from peer-controlled data.

import { EMOJI_LIST, EMOJIS_PER_PAGE, AI } from './config.js';
import { getMutableState, update } from './state.js';
import { el, linkify, clearChildren, showNotification } from './utils.js';
import { sendData } from './rtc.js';
import { ask as aiAsk, isConfigured as aiIsConfigured } from './ai.js';
import { showAISettingsModal } from './ui.js';

/** DOM element references — set via init() */
let chatMessages = null;
let chatInput = null;
let emojiPickerContent = null;
let emojiPickerPagination = null;

/** Unread message tracking */
let unreadCount = 0;
let isChatScrolledToBottom = true;
let unreadBadge = null;

/**
 * Initialize chat module with DOM element references.
 */
export function init(elements) {
  chatMessages = elements.chatMessages;
  chatInput = elements.chatInput;
  emojiPickerContent = elements.emojiPickerContent;
  emojiPickerPagination = elements.emojiPickerPagination;

  // Track scroll position to detect when user is not at bottom
  if (chatMessages) {
    chatMessages.addEventListener('scroll', () => {
      const atBottom =
        chatMessages.scrollHeight - chatMessages.scrollTop - chatMessages.clientHeight < 40;
      isChatScrolledToBottom = atBottom;
      if (atBottom && unreadBadge) {
        unreadCount = 0;
        updateUnreadBadge();
      }
    });
  }

  showEmptyState();
}

/**
 * Add a chat message to the log using safe DOM APIs.
 * Supports linkification for local messages and validated peer messages.
 *
 * @param {string} message — plain text message content
 * @param {boolean} isOwn — true if sent by local user
 * @param {string} author — display name of the author
 */
export function addChatMessage(message, isOwn, author = 'Peer') {
  if (!chatMessages) return;

  const wrapper = el('div', {
    className: `chat-message ${isOwn ? 'own' : 'other'}`,
    role: 'listitem',
  });

  // Author label — safe: we control isOwn, and author is validated by protocol
  const authorLabel = el('strong', {
    textContent: isOwn ? 'You' : author,
  });

  wrapper.appendChild(authorLabel);
  wrapper.appendChild(document.createTextNode(': '));

  // Message content — linkified safely via DOM nodes
  wrapper.appendChild(linkify(message));

  hideEmptyState();
  chatMessages.appendChild(wrapper);
  scrollToBottomOrTrackUnread();
}

/**
 * Add a system/command message to the chat log.
 * These are LOCAL-ONLY formatted messages. The text content comes from
 * our own code, not from peer data directly.
 *
 * For peer-originated commands: the text was validated by protocol.js
 * and we render it as plain text only.
 *
 * @param {string} text — plain text command result
 */
export function addCommandMessage(text) {
  if (!chatMessages) return;

  const wrapper = el('div', {
    className: 'chat-message command',
    role: 'listitem',
  });

  wrapper.textContent = text;

  chatMessages.appendChild(wrapper);
  hideEmptyState();
  scrollToBottomOrTrackUnread();
}

/**
 * Add a keyboard shortcuts help message to the chat using safe DOM.
 */
export function addShortcutsMessage() {
  if (!chatMessages) return;

  const wrapper = el('div', {
    className: 'chat-message command',
    role: 'listitem',
  });

  const title = el('div', {
    textContent: 'Keyboard Shortcuts',
    style: { marginBottom: '0.3rem', fontWeight: 'bold' },
  });
  wrapper.appendChild(title);

  const shortcuts = [
    ['M', 'Toggle Mute'],
    ['V', 'Toggle Video'],
    ['S', 'Split View'],
    ['F', 'Fullscreen (not in split)'],
    ['R', 'Toggle Screen Share'],
    ['P', 'Toggle Picture-in-Picture'],
    ['T', 'Take Snapshot'],
    ['E', 'End Call (Shift+E to skip confirm)'],
    ['ESC', 'Exit Fullscreen'],
    ['Ctrl+C', 'Copy Link'],
    ['Ctrl+D', 'Toggle Theme'],
    ['Shift+?', 'Show this help'],
  ];

  for (const [key, desc] of shortcuts) {
    const line = el('div', {
      children: [el('strong', { textContent: key }), document.createTextNode(` — ${desc}`)],
    });
    wrapper.appendChild(line);
  }

  hideEmptyState();
  chatMessages.appendChild(wrapper);
  scrollToBottomOrTrackUnread();
}

/**
 * Add a help/commands listing message to the chat.
 */
export function addHelpMessage() {
  if (!chatMessages) return;

  const wrapper = el('div', {
    className: 'chat-message command',
    role: 'listitem',
  });

  const title = el('div', {
    textContent: 'Available Commands:',
    style: { marginBottom: '0.3rem', fontWeight: 'bold' },
  });
  wrapper.appendChild(title);

  const commands = [
    ['/ask <question>', 'Ask AI assistant'],
    ['/ai', 'Configure AI settings'],
    ['/roll [max] or [min-max]', 'Roll dice'],
    ['/coin', 'Flip a coin'],
    ['/pyah', 'Emoji blast'],
    ['/clear', 'Clear chat messages'],
    ['/keys', 'Show keyboard shortcuts'],
    ['/help', 'Show this menu'],
  ];

  for (const [cmd, desc] of commands) {
    const line = el('div', {
      children: [el('strong', { textContent: cmd }), document.createTextNode(` — ${desc}`)],
    });
    wrapper.appendChild(line);
  }

  hideEmptyState();
  chatMessages.appendChild(wrapper);
  scrollToBottomOrTrackUnread();
}

/**
 * Show floating emoji reaction animation.
 *
 * @param {string} emoji — validated emoji character
 * @param {'local'|'remote'} target — which video container
 * @param {number} [intensity=1] — multiplier for particle count
 */
export function showEmojiReaction(emoji, target, intensity = 1) {
  const containerId = target === 'local' ? 'localEmojiContainer' : 'remoteEmojiContainer';
  const container = document.getElementById(containerId);
  if (!container) return;

  const count = Math.floor(8 * intensity);
  const PER_FRAME = 2;
  let created = 0;

  function createBatch() {
    const end = Math.min(created + PER_FRAME, count);
    for (let i = created; i < end; i++) {
      const particle = el('div', {
        textContent: emoji,
        style: {
          position: 'absolute',
          fontSize: '2rem',
          left: `${Math.random() * 80 + 10}%`,
          top: `${Math.random() * 50 + 40}%`,
          pointerEvents: 'none',
        },
      });
      container.appendChild(particle);

      const dx = (Math.random() - 0.5) * 200;
      const dy = -300 - Math.random() * 200;
      particle.animate(
        [
          { transform: 'translate(0,0)', opacity: 1 },
          { transform: `translate(${dx}px, ${dy}px)`, opacity: 0 },
        ],
        { duration: 2000 + Math.random() * 1000, easing: 'ease-out' },
      );
      setTimeout(() => particle.remove(), 3000);
    }
    created = end;
    if (created < count) requestAnimationFrame(createBatch);
  }

  requestAnimationFrame(createBatch);
}

/**
 * Trigger celebration effect on both video containers.
 */
export function triggerCelebration(emoji = '🎉') {
  showEmojiReaction(emoji, 'local', 2);
  showEmojiReaction(emoji, 'remote', 2);
}

/**
 * Roll dice based on args. Pure function for testability.
 * @param {string[]} args — command args (e.g. [], ['50'], ['10-20'], ['100-1'])
 * @param {string} nickname — user's display name
 * @returns {{ result: number, max: number, text: string, isCritical: boolean }}
 */
export function rollDice(args, nickname) {
  let rollResult = 0;
  let rollMax = 100;
  let rangeText = '1-100';

  if (args.length === 0) {
    rollResult = Math.floor(Math.random() * 100) + 1;
  } else if (args[0] && args[0].includes('-')) {
    const split = args[0].split('-').map(Number);
    let min = split[0] || 1;
    let max = split[1] || 100;
    if (min > max) [min, max] = [max, min];
    rollMax = max;
    rollResult = Math.floor(Math.random() * (max - min + 1)) + min;
    rangeText = `${min}-${max}`;
  } else {
    const max = parseInt(args[0], 10) || 100;
    rollMax = max;
    rollResult = Math.floor(Math.random() * max) + 1;
    rangeText = `1-${max}`;
  }

  const isCritical = rollResult === rollMax;
  let text = `🎲 ${nickname} rolls ${rollResult} (${rangeText})`;
  if (isCritical) text += ' 🎉 CRITICAL SUCCESS!';

  return { result: rollResult, max: rollMax, text, isCritical };
}

/**
 * Process a slash command entered by the local user.
 * Returns the command result for local display and optional peer broadcast.
 */
export function handleSlashCommand(command) {
  const state = getMutableState();
  const parts = command.split(' ');
  const cmd = parts[0].toLowerCase();
  const args = parts.slice(1);
  let resultText = '';
  let isCelebration = false;

  switch (cmd) {
    case '/help':
    case '/commands':
      addHelpMessage();
      return;

    case '/keys':
      addShortcutsMessage();
      return;

    case '/clear':
      clearChatMessages();
      return;

    case '/roll': {
      const roll = rollDice(args, state.localNickname);
      resultText = roll.text;
      if (roll.isCritical) isCelebration = true;
      break;
    }

    case '/coin':
      resultText = `🪙 ${state.localNickname} flips a coin: ${Math.random() < 0.5 ? 'HEADS' : 'TAILS'}!`;
      break;

    case '/pyah':
      resultText = `💥 ${state.localNickname} sends a blast of emojis!`;
      EMOJI_LIST.slice(0, 10).forEach((emo) => {
        showEmojiReaction(emo, 'local', 0.5);
        sendData({ type: 'pyah', emoji: emo });
      });
      break;

    case '/ai':
      showAISettingsModal();
      return;

    case '/ask': {
      const query = args.join(' ').trim();
      if (!query) {
        addCommandMessage('Usage: /ask <question>');
        return;
      }
      if (query.length > AI.MAX_QUERY_LENGTH) {
        addCommandMessage(`Query too long (max ${AI.MAX_QUERY_LENGTH} chars)`);
        return;
      }
      if (!aiIsConfigured()) {
        addCommandMessage('AI not configured. Type /ai to set your API key.');
        showAISettingsModal();
        return;
      }
      handleAIQuery(query);
      return;
    }

    default:
      resultText = `Unknown command: ${cmd}`;
  }

  if (resultText) {
    addCommandMessage(resultText);
    sendData({ type: 'command', message: resultText });
    if (isCelebration) {
      triggerCelebration();
      sendData({ type: 'celebration', emoji: '🎉' });
    }
  }
}

/**
 * Send a chat message or slash command from the input.
 * Returns false if message was not deliverable (channel not open).
 */
export function sendMessage() {
  if (!chatInput) return false;
  const message = chatInput.value.trim();
  if (!message) return false;

  if (message.startsWith('/')) {
    handleSlashCommand(message);
    chatInput.value = '';
    return true;
  }

  const state = getMutableState();

  // Check if data channel is open before claiming message sent
  const sent = sendData({ type: 'chat', message, nickname: state.localNickname });
  if (!sent) {
    showNotification('Message not sent — peer not connected', 'error');
    return false;
  }

  addChatMessage(message, true, state.localNickname);
  chatInput.value = '';
  return true;
}

/**
 * Handle an AI query — shows a streaming response in the chat.
 * @param {string} query
 */
function handleAIQuery(query) {
  if (!chatMessages) return;

  hideEmptyState();

  // User question bubble
  const questionWrapper = el('div', {
    className: 'chat-message own ai-question',
    role: 'listitem',
  });
  questionWrapper.appendChild(el('strong', { textContent: 'You asked AI' }));
  questionWrapper.appendChild(document.createTextNode(': '));
  questionWrapper.appendChild(document.createTextNode(query));
  chatMessages.appendChild(questionWrapper);

  // AI response wrapper (streaming)
  const responseWrapper = el('div', {
    className: 'chat-message ai-response',
    role: 'listitem',
  });

  const header = el('div', {
    className: 'ai-response-header',
    children: [
      el('span', { className: 'ai-badge', textContent: '🤖 AI' }),
      el('span', { className: 'ai-status', textContent: 'Thinking…' }),
    ],
  });
  responseWrapper.appendChild(header);

  const contentDiv = el('div', {
    className: 'ai-response-content',
    textContent: '',
  });
  responseWrapper.appendChild(contentDiv);

  chatMessages.appendChild(responseWrapper);
  scrollToBottomOrTrackUnread();

  const statusSpan = responseWrapper.querySelector('.ai-status');
  let firstChunk = true;

  aiAsk(
    query,
    (delta) => {
      if (firstChunk) {
        firstChunk = false;
        if (statusSpan) statusSpan.textContent = '';
      }
      contentDiv.textContent += delta;
      scrollToBottomOrTrackUnread();
    },
    (fullText) => {
      if (statusSpan) statusSpan.textContent = '';
      if (!fullText) {
        contentDiv.textContent = '(empty response)';
      }
      responseWrapper.classList.add('ai-response-done');
      scrollToBottomOrTrackUnread();
    },
    (errorMsg) => {
      if (statusSpan) statusSpan.remove();
      contentDiv.textContent = `⚠️ ${errorMsg}`;
      responseWrapper.classList.add('ai-response-error');
      scrollToBottomOrTrackUnread();
    },
  );
}

/**
 * Handle an incoming validated data message from a peer.
 * Called by rtc.js after protocol validation.
 */
export function handlePeerMessage(data) {
  switch (data.type) {
    case 'chat':
      addChatMessage(data.message, false, data.nickname);
      break;
    case 'command':
      // Peer commands rendered as plain text only (no HTML formatting)
      addCommandMessage(data.message);
      break;
    case 'emoji':
      showEmojiReaction(data.emoji, 'remote');
      break;
    case 'nickname':
      update({ remoteNickname: data.nickname });
      break;
    case 'pyah':
      showEmojiReaction(data.emoji, 'remote', 0.5);
      break;
    case 'celebration':
      triggerCelebration(data.emoji);
      break;
    case 'presence':
      // Could be used for typing indicators, etc.
      break;
  }
}

/**
 * Render the emoji picker with pagination using safe DOM.
 */
export function renderEmojiPicker() {
  if (!emojiPickerContent || !emojiPickerPagination) return;

  const state = getMutableState();
  const totalPages = Math.ceil(EMOJI_LIST.length / EMOJIS_PER_PAGE);
  const startIdx = state.currentEmojiPage * EMOJIS_PER_PAGE;
  const pageEmojis = EMOJI_LIST.slice(startIdx, startIdx + EMOJIS_PER_PAGE);

  clearChildren(emojiPickerContent);
  for (const emoji of pageEmojis) {
    const btn = el('button', {
      className: 'emoji-btn',
      textContent: emoji,
      'aria-label': `Send ${emoji} reaction`,
      events: {
        click: (e) => {
          e.stopPropagation();
          showEmojiReaction(emoji, 'local');
          sendData({ type: 'emoji', emoji });
          update({ isEmojiPickerOpen: false });
          if (chatInput) chatInput.focus();
        },
      },
    });
    emojiPickerContent.appendChild(btn);
  }

  clearChildren(emojiPickerPagination);
  for (let i = 0; i < totalPages; i++) {
    const pageBtn = el('button', {
      className: `page-btn ${i === state.currentEmojiPage ? 'active' : ''}`,
      textContent: String(i + 1),
      'aria-label': `Emoji page ${i + 1}`,
      events: {
        click: (e) => {
          e.stopPropagation();
          update({ currentEmojiPage: i });
          renderEmojiPicker();
        },
      },
    });
    emojiPickerPagination.appendChild(pageBtn);
  }
}

/**
 * Show the empty state hint when no messages exist.
 */
function showEmptyState() {
  if (!chatMessages) return;
  if (chatMessages.querySelector('.chat-empty-state')) return;

  const hint = el('div', {
    className: 'chat-empty-state',
    textContent: 'Messages appear here. Say hello or type /help for commands.',
  });
  chatMessages.appendChild(hint);
}

/**
 * Remove the empty state hint if present.
 */
function hideEmptyState() {
  if (!chatMessages) return;
  const hint = chatMessages.querySelector('.chat-empty-state');
  if (hint) hint.remove();
}

/**
 * Clear all chat messages and restore the empty state.
 */
function clearChatMessages() {
  if (!chatMessages) return;
  clearChildren(chatMessages);
  unreadCount = 0;
  isChatScrolledToBottom = true;
  if (unreadBadge) {
    unreadBadge.remove();
    unreadBadge = null;
  }
  showEmptyState();
}

/**
 * Scroll to bottom if already at bottom; otherwise track unread count.
 */
function scrollToBottomOrTrackUnread() {
  if (!chatMessages) return;
  if (isChatScrolledToBottom) {
    chatMessages.scrollTop = chatMessages.scrollHeight;
  } else {
    unreadCount++;
    updateUnreadBadge();
  }
}

/**
 * Update or create the unread messages badge on the chat section.
 */
function updateUnreadBadge() {
  if (!chatMessages) return;
  const chatSection = chatMessages.closest('.chat-section');
  if (!chatSection) return;

  if (unreadCount === 0) {
    if (unreadBadge) {
      unreadBadge.remove();
      unreadBadge = null;
    }
    return;
  }

  if (!unreadBadge) {
    unreadBadge = el('button', {
      className: 'chat-unread-badge',
      'aria-label': `${unreadCount} unread messages — jump to latest`,
      events: {
        click: () => {
          chatMessages.scrollTop = chatMessages.scrollHeight;
          unreadCount = 0;
          isChatScrolledToBottom = true;
          updateUnreadBadge();
        },
      },
    });
    chatSection.appendChild(unreadBadge);
  }
  unreadBadge.textContent = `↓ ${unreadCount} unread`;
}
